import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SessionService } from '../services/session.service';
import { TranslationService } from '../services/translation.service';
import { TranslatePipe } from '../pipes/translate.pipe';
import {
  DailyCheckinEntry,
  DailyCheckinService,
  CheckinSection,
  JournalDay,
  JournalModel,
  JournalSummary,
  JournalVoice,
} from '../services/daily-checkin.service';
import { SpeechToTextService } from '../services/speech-to-text.service';
import {
  WellnessTipsService,
  WellnessTipKey,
} from '../services/wellness-tips.service';
import { WellnessWidgetService } from '../services/wellness-widget.service';
import { ErrorReportingService } from '../services/error-reporting.service';
import { MappingTelemetryService } from '../services/mapping-telemetry.service';
import { formatFirstNameForDisplay } from '../shared/name-display';

interface VoiceStep {
  section: CheckinSection;
  titleKey: string;
  instructionKey: string;
  partKeys: string[];
  helperKey: string;
  canSkip?: boolean;
}

interface SliderDef {
  key: string;
  labelKey: string;
  min: number;
  max: number;
  default: number;
  format: 'time' | 'number';
}

interface GridItem {
  icon: string;
  labelKey: string;
  value: string;
}

interface WrittenStep {
  section: CheckinSection;
  labelKey: string;
  questionKey: string;
  sliders?: SliderDef[];
  grid?: GridItem[];
  textareaTitleKey: string;
  placeholderKey: string;
}

type View = 'home' | 'voice' | 'written' | 'done' | 'journal';
type RecordingStatus =
  | 'idle'
  | 'recording'
  | 'paused'
  | 'transcribing'
  | 'done';

@Component({
  selector: 'app-daily-checkin',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    TranslatePipe,
  ],
  templateUrl: './daily-checkin.component.html',
  styleUrl: './daily-checkin.component.scss',
})
export class DailyCheckinComponent implements OnInit, OnDestroy {
  view: View = 'home';
  firstName = '';
  doneTipKey: WellnessTipKey | null = null;

  stepIndex = 0;

  // Voice state
  voiceStatus: RecordingStatus = 'idle';
  voiceInterim = '';
  voiceError = '';
  /** B7: server transcription failed. The audio is kept in memory so Retry
   *  re-sends exactly what she recorded instead of asking her to record again. */
  transcribeFailed = false;
  transcribeRetrying = false;
  private failedAudio: {
    base64: string;
    mime: string;
    stepIndex: number;
  } | null = null;
  private voiceResults: Array<{
    transcript: string;
    audioMimeType: string;
    durationMs: number;
    audioToken?: string;
    skipped?: boolean;
  }> = [];

  // Written state
  writtenSliderValues: Record<string, Record<string, number>> = {};
  writtenSelectedTags: Record<string, string[]> = {};
  writtenText: Record<string, string> = {};

  saving = false;

  // -------- Journal state --------
  journalDays: JournalDay[] = [];
  journalSummary: JournalSummary | null = null;
  journalSummaryText = '';
  journalLoading = false;
  journalLoaded = false;
  journalError = false;
  journalVisibleCount = 7;
  private readonly JOURNAL_PAGE = 7;
  private audioEl: HTMLAudioElement | null = null;
  private playingVoice: JournalVoice | null = null;
  savingTranscript = false;

  private readonly VOICE_STATE_KEY = 'dailyCheckin.voiceState.v1';

  readonly voiceSteps: VoiceStep[] = [
    {
      section: 'sleep',
      titleKey: 'dailyCheckin.voice.sleep.title',
      instructionKey: 'dailyCheckin.voice.sleep.instruction',
      partKeys: [
        'dailyCheckin.voice.sleep.part1',
        'dailyCheckin.voice.sleep.part2',
        'dailyCheckin.voice.sleep.part3',
        'dailyCheckin.voice.sleep.part4',
      ],
      helperKey: 'dailyCheckin.voice.sleep.helper',
    },
    {
      section: 'mood',
      titleKey: 'dailyCheckin.voice.mood.title',
      instructionKey: 'dailyCheckin.voice.mood.instruction',
      partKeys: [
        'dailyCheckin.voice.mood.part1',
        'dailyCheckin.voice.mood.part2',
      ],
      helperKey: 'dailyCheckin.voice.mood.helper',
      canSkip: true,
    },
    {
      section: 'physical',
      titleKey: 'dailyCheckin.voice.physical.title',
      instructionKey: 'dailyCheckin.voice.physical.instruction',
      partKeys: [],
      helperKey: 'dailyCheckin.voice.physical.helper',
    },
  ];

  readonly writtenSteps: WrittenStep[] = [
    {
      section: 'sleep',
      labelKey: 'dailyCheckin.written.sleep.label',
      questionKey: 'dailyCheckin.written.sleep.question',
      sliders: [
        {
          key: 'fallAsleep',
          labelKey: 'dailyCheckin.written.sleep.sliderFallAsleep',
          min: 18,
          max: 30,
          default: 23,
          format: 'time',
        },
        {
          key: 'wakeUp',
          labelKey: 'dailyCheckin.written.sleep.sliderWakeUp',
          min: 4,
          max: 14,
          default: 7,
          format: 'time',
        },
      ],
      grid: [
        {
          icon: 'bedtime_off',
          labelKey: 'dailyCheckin.written.sleep.tagHardToSleep',
          value: 'hard_to_sleep',
        },
        {
          icon: 'visibility',
          labelKey: 'dailyCheckin.written.sleep.tagWokeDuringNight',
          value: 'woke_during_night',
        },
        {
          icon: 'mood_bad',
          labelKey: 'dailyCheckin.written.sleep.tagWokeTired',
          value: 'woke_tired',
        },
        {
          icon: 'cyclone',
          labelKey: 'dailyCheckin.written.sleep.tagRestless',
          value: 'restless',
        },
        {
          icon: 'wb_sunny',
          labelKey: 'dailyCheckin.written.sleep.tagRefreshed',
          value: 'refreshed',
        },
      ],
      textareaTitleKey: 'dailyCheckin.written.sleep.textareaTitle',
      placeholderKey: 'dailyCheckin.written.sleep.placeholder',
    },
    {
      section: 'mood',
      labelKey: 'dailyCheckin.written.mood.label',
      questionKey: 'dailyCheckin.written.mood.question',
      grid: [
        {
          icon: 'wb_sunny',
          labelKey: 'dailyCheckin.written.mood.tagAllGood',
          value: 'all_good',
        },
        {
          icon: 'auto_awesome',
          labelKey: 'dailyCheckin.written.mood.tagUplifted',
          value: 'uplifted',
        },
        {
          icon: 'filter_vintage',
          labelKey: 'dailyCheckin.written.mood.tagSensitive',
          value: 'sensitive',
        },
        {
          icon: 'bolt',
          labelKey: 'dailyCheckin.written.mood.tagIrritable',
          value: 'irritable',
        },
        {
          icon: 'sentiment_very_dissatisfied',
          labelKey: 'dailyCheckin.written.mood.tagAnxious',
          value: 'anxious',
        },
        {
          icon: 'cloud',
          labelKey: 'dailyCheckin.written.mood.tagLow',
          value: 'low',
        },
        {
          icon: 'swap_vert',
          labelKey: 'dailyCheckin.written.mood.tagUpAndDown',
          value: 'up_and_down',
        },
        {
          icon: 'waves',
          labelKey: 'dailyCheckin.written.mood.tagOverwhelmed',
          value: 'overwhelmed',
        },
      ],
      textareaTitleKey: 'dailyCheckin.written.mood.textareaTitle',
      placeholderKey: 'dailyCheckin.written.mood.placeholder',
    },
    {
      section: 'physical',
      labelKey: 'dailyCheckin.written.physical.label',
      questionKey: 'dailyCheckin.written.physical.question',
      textareaTitleKey: 'dailyCheckin.written.physical.textareaTitle',
      placeholderKey: 'dailyCheckin.written.physical.placeholder',
    },
  ];

  constructor(
    private router: Router,
    private sessionService: SessionService,
    private dailyCheckinService: DailyCheckinService,
    private speech: SpeechToTextService,
    private translation: TranslationService,
    private wellnessTips: WellnessTipsService,
    private widget: WellnessWidgetService,
    private reporter: ErrorReportingService,
    private telemetry: MappingTelemetryService,
  ) {}

  ngOnInit(): void {
    this.firstName =
      formatFirstNameForDisplay(this.sessionService.session?.user?.fname) ||
      this.translation.translate('dailyCheckin.greetingFallback');

    this.writtenSteps.forEach((step) => {
      this.writtenSelectedTags[step.section] = [];
      this.writtenText[step.section] = '';
      if (step.sliders) {
        this.writtenSliderValues[step.section] = {};
        step.sliders.forEach((s) => {
          this.writtenSliderValues[step.section][s.key] = s.default;
        });
      }
    });

    this.restoreVoiceState();
  }

  ngOnDestroy(): void {
    this.speech.cancel();
    this.stopAudio();
  }

  t(key: string, options?: any): string {
    return this.translation.translate(key, options);
  }

  // -------- Home screen --------
  startVoice(): void {
    this.telemetry.step('daily_checkin');
    this.telemetry.track('daily_checkin_started', { mode: 'voice' });
    this.stepIndex = 0;
    this.voiceResults = this.voiceSteps.map(() => ({
      transcript: '',
      audioMimeType: '',
      durationMs: 0,
    }));
    this.resetVoiceRecordingUI();
    this.view = 'voice';
    this.persistVoiceState();
  }

  startWritten(): void {
    this.telemetry.step('daily_checkin');
    this.telemetry.track('daily_checkin_started', { mode: 'written' });
    this.stepIndex = 0;
    this.view = 'written';
  }

  openFullCheckin(): void {
    this.router.navigate(['/app/new-test']);
  }

  // B2 (focused-task mode): the bottom nav is hidden during the check-in, so the
  // home view needs its own explicit way out. Top-left exit -> app home.
  exitCheckin(): void {
    this.router.navigate(['/', this.translation.getCurrentLanguage(), 'app', 'home']);
  }

  // -------- Voice flow --------
  get currentVoiceStep(): VoiceStep {
    return this.voiceSteps[this.stepIndex];
  }

  voiceBack(): void {
    this.speech.cancel();
    if (this.stepIndex > 0) {
      this.stepIndex--;
      this.resetVoiceRecordingUI();
      this.persistVoiceState();
    } else {
      this.view = 'home';
      this.clearVoiceState();
    }
  }

  async onMicClick(): Promise<void> {
    if (this.voiceStatus === 'idle') {
      await this.startRecording();
    } else if (this.voiceStatus === 'paused') {
      this.resumeRecording();
    } else if (this.voiceStatus === 'recording') {
      this.pauseRecording();
    }
  }

  async restartRecording(): Promise<void> {
    this.speech.cancel();
    this.voiceResults[this.stepIndex] = {
      transcript: '',
      audioMimeType: '',
      durationMs: 0,
      audioToken: undefined,
    };
    this.voiceInterim = '';
    this.voiceStatus = 'idle';
    this.persistVoiceState();
    await this.startRecording();
  }

  async startRecording(): Promise<void> {
    this.voiceError = '';
    this.voiceInterim = '';
    this.reporter.logEvent('checkin.record.start', { step: this.stepIndex });
    try {
      await this.speech.start((text) => (this.voiceInterim = text));
      this.voiceStatus = 'recording';
    } catch (e: any) {
      console.error('Speech start failed:', e);
      this.reporter.report(e, 'checkin');
      this.voiceError =
        e?.name === 'NotAllowedError'
          ? this.t('dailyCheckin.mic.permissionDenied')
          : this.t('dailyCheckin.mic.micError');
      this.voiceStatus = 'idle';
    }
  }

  pauseRecording(): void {
    if (this.voiceStatus === 'recording') {
      this.speech.pause();
      this.voiceStatus = 'paused';
    }
  }

  resumeRecording(): void {
    if (this.voiceStatus === 'paused') {
      this.speech.resume((text) => (this.voiceInterim = text));
      this.voiceStatus = 'recording';
    }
  }

  async finishRecording(): Promise<void> {
    if (this.voiceStatus !== 'recording' && this.voiceStatus !== 'paused') {
      return;
    }
    this.transcribeFailed = false;
    this.failedAudio = null;
    const hasInterim = !!this.voiceInterim;
    this.voiceStatus = hasInterim ? 'done' : 'transcribing';
    try {
      const result = await this.speech.stop();
      const browserTranscript = result.interimTranscript || '';
      const stepIndex = this.stepIndex;

      if (browserTranscript) {
        this.voiceResults[stepIndex] = {
          transcript: browserTranscript,
          audioMimeType: result.mimeType,
          durationMs: result.durationMs,
          audioToken: undefined,
        };
        this.voiceStatus = 'done';
        this.persistVoiceState();

        if (result.audioBase64) {
          this.dailyCheckinService
            .transcribe(result.audioBase64, result.mimeType, browserTranscript)
            .then((resp) => {
              const current = this.voiceResults[stepIndex];
              if (current && resp.audioToken) {
                current.audioToken = resp.audioToken;
                this.persistVoiceState();
              }
            })
            .catch((e) => {
              console.warn('Audio upload failed:', e);
              this.reporter.report(e, 'checkin');
            });
        }
        return;
      }

      let finalTranscript = '';
      let audioToken: string | undefined;
      if (result.audioBase64) {
        try {
          const resp = await this.dailyCheckinService.transcribe(
            result.audioBase64,
            result.mimeType,
          );
          if (resp.transcript) finalTranscript = resp.transcript;
          audioToken = resp.audioToken;
        } catch (e) {
          console.warn('Server transcription failed:', e);
          this.reporter.report(e, 'checkin');
          // B7: keep the audio so she can retry the transcription itself.
          this.transcribeFailed = true;
          this.failedAudio = {
            base64: result.audioBase64,
            mime: result.mimeType,
            stepIndex,
          };
        }
      }

      this.voiceResults[stepIndex] = {
        transcript: finalTranscript,
        audioMimeType: result.mimeType,
        durationMs: result.durationMs,
        audioToken,
      };
      this.voiceStatus = 'done';
      this.persistVoiceState();
    } catch (e) {
      console.error('Finish recording failed:', e);
      this.reporter.report(e, 'checkin');
      this.voiceStatus = 'idle';
      this.voiceError = this.t('dailyCheckin.mic.recordFailed');
    }
  }

  /** B7: re-run the transcription on the audio she already recorded. */
  async retryTranscription(): Promise<void> {
    if (this.transcribeRetrying || !this.failedAudio) return;
    this.transcribeRetrying = true;
    const audio = this.failedAudio;
    try {
      const resp = await this.dailyCheckinService.transcribe(
        audio.base64,
        audio.mime,
      );
      const entry = this.voiceResults[audio.stepIndex];
      if (entry) {
        if (resp.transcript) entry.transcript = resp.transcript;
        if (resp.audioToken) entry.audioToken = resp.audioToken;
        this.persistVoiceState();
      }
      this.transcribeFailed = false;
      this.failedAudio = null;
    } catch (e) {
      console.warn('Server transcription retry failed:', e);
      this.reporter.report(e, 'checkin');
    } finally {
      this.transcribeRetrying = false;
    }
  }

  skipVoiceStep(): void {
    this.voiceResults[this.stepIndex] = {
      transcript: '',
      audioMimeType: '',
      durationMs: 0,
      skipped: true,
    };
    this.persistVoiceState();
    this.advanceVoice();
  }

  nextVoiceStep(): void {
    if (this.voiceStatus === 'done') {
      this.advanceVoice();
    }
  }

  private advanceVoice(): void {
    if (this.stepIndex < this.voiceSteps.length - 1) {
      this.stepIndex++;
      this.resetVoiceRecordingUI();
      this.persistVoiceState();
    } else {
      this.submitVoice();
    }
  }

  private resetVoiceRecordingUI(): void {
    this.voiceStatus = 'idle';
    this.voiceInterim = '';
    this.voiceError = '';
  }

  private async submitVoice(): Promise<void> {
    const entries: DailyCheckinEntry[] = this.voiceSteps.map((step, i) => {
      const r = this.voiceResults[i];
      return {
        section: step.section,
        question: this.t(step.titleKey),
        mode: 'voice',
        transcript: r?.transcript || '',
        audioMimeType: r?.audioMimeType,
        durationMs: r?.durationMs,
        audioToken: r?.audioToken,
      };
    });
    await this.saveAndFinish({ mode: 'voice', entries });
  }

  // -------- Written flow --------
  get currentWrittenStep(): WrittenStep {
    return this.writtenSteps[this.stepIndex];
  }

  writtenBack(): void {
    if (this.stepIndex > 0) {
      this.stepIndex--;
    } else {
      this.view = 'home';
    }
  }

  toggleTag(section: CheckinSection, value: string): void {
    const arr = this.writtenSelectedTags[section];
    const i = arr.indexOf(value);
    if (i >= 0) arr.splice(i, 1);
    else arr.push(value);
  }

  isTagSelected(section: CheckinSection, value: string): boolean {
    return this.writtenSelectedTags[section].includes(value);
  }

  formatSliderValue(step: WrittenStep, slider: SliderDef): string {
    const val = this.writtenSliderValues[step.section][slider.key];
    if (slider.format === 'time') {
      const hour = ((val % 24) + 24) % 24;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const display = hour % 12 || 12;
      return `${display}:00 ${ampm}`;
    }
    return `${val}`;
  }

  onSliderInput(step: WrittenStep, slider: SliderDef, event: Event): void {
    const target = event.target as HTMLInputElement;
    this.writtenSliderValues[step.section][slider.key] = Number(target.value);
  }

  nextWrittenStep(): void {
    if (this.stepIndex < this.writtenSteps.length - 1) {
      this.stepIndex++;
    } else {
      this.submitWritten();
    }
  }

  private async submitWritten(): Promise<void> {
    const entries: DailyCheckinEntry[] = this.writtenSteps.map((step) => {
      const entry: DailyCheckinEntry = {
        section: step.section,
        question: this.t(step.questionKey),
        mode: 'written',
      };
      if (step.sliders) {
        entry.sliders = step.sliders.map((s) => ({
          label: this.t(s.labelKey),
          value: this.formatSliderValue(step, s),
        }));
      }
      const tagValues = this.writtenSelectedTags[step.section];
      if (tagValues && tagValues.length && step.grid) {
        entry.tags = tagValues.map((v) => {
          const item = step.grid!.find((g) => g.value === v);
          return item ? this.t(item.labelKey) : v;
        });
      }
      const text = this.writtenText[step.section]?.trim();
      if (text) entry.text = text;
      return entry;
    });
    await this.saveAndFinish({ mode: 'written', entries });
  }

  // -------- Shared submission --------
  private async saveAndFinish(payload: {
    mode: 'voice' | 'written';
    entries: DailyCheckinEntry[];
  }): Promise<void> {
    if (this.saving) return;
    this.saving = true;
    try {
      await this.dailyCheckinService.save(payload);
      // section 43: the mode and how many prompts she answered, never the words
      this.telemetry.track('daily_checkin_completed', {
        mode: payload.mode,
        entries: payload.entries.length,
      });
      // Diary completion is recorded server-side (no markDone here), so stamp a
      // local hint + repaint the widget's check-in tile immediately.
      WellnessWidgetService.markCheckinLocal();
      this.widget.refresh();
    } catch (e) {
      console.error('Saving check-in failed:', e);
      this.reporter.report(e, 'checkin');
    } finally {
      this.saving = false;
      this.clearVoiceState();
      this.showDoneView();
    }
  }

  finishDone(): void {
    this.view = 'home';
    this.doneTipKey = null;
  }

  // -------- Journal --------
  openJournal(): void {
    this.stopAudio();
    this.doneTipKey = null;
    this.view = 'journal';
    this.journalVisibleCount = this.JOURNAL_PAGE;
    // Always refresh when coming from a just-finished check-in so the new entry
    // is on top; otherwise reuse the cached model if we already have it.
    if (!this.journalLoaded || !this.journalLoading) {
      this.loadJournal();
    }
  }

  async loadJournal(): Promise<void> {
    if (this.journalLoading) return;
    this.journalLoading = true;
    this.journalError = false;
    try {
      const list = await this.dailyCheckinService.getHistory();
      const model: JournalModel = this.dailyCheckinService.buildJournal(
        list,
        this.translation.getCurrentLanguage(),
      );
      this.journalDays = model.days;
      this.journalSummary = model.summary;
      this.journalSummaryText = this.buildSummaryText(model.summary);
      this.journalLoaded = true;
    } catch (e) {
      console.error('Loading journal failed:', e);
      this.reporter.report(e, 'checkin');
      this.journalError = true;
    } finally {
      this.journalLoading = false;
    }
  }

  journalBack(): void {
    this.stopAudio();
    this.view = 'home';
  }

  // §5: soft narrative built only from structured data (chips + sleep sliders).
  private buildSummaryText(s: JournalSummary | null): string {
    if (!s) return '';
    const parts: string[] = [];
    if (s.symptom) {
      parts.push(
        this.t('dailyCheckin.journal.weekSymptom', {
          symptom: s.symptom,
          days: s.symptomDays,
          total: s.totalDays,
        }),
      );
    }
    if (s.sleepMin != null && s.sleepMax != null) {
      parts.push(
        this.t(
          s.symptom
            ? 'dailyCheckin.journal.weekSleepJoin'
            : 'dailyCheckin.journal.weekSleep',
          { min: s.sleepMin, max: s.sleepMax },
        ),
      );
    }
    let sentence = parts.join('').trim();
    if (!sentence) return '';
    if (!/[.!?]$/.test(sentence)) sentence += '.';
    return `${sentence} ${this.t('dailyCheckin.journal.weekPattern')}`.trim();
  }

  get visibleJournalDays(): JournalDay[] {
    return this.journalDays.slice(0, this.journalVisibleCount);
  }

  get hasMoreDays(): boolean {
    return this.journalDays.length > this.journalVisibleCount;
  }

  showMoreDays(): void {
    this.journalVisibleCount += this.JOURNAL_PAGE;
  }

  // Show a time separator above the first day of each bucket only.
  isFirstOfBucket(index: number): boolean {
    const days = this.visibleJournalDays;
    if (index === 0) return true;
    return days[index].bucket !== days[index - 1].bucket;
  }

  bucketLabelKey(bucket: JournalDay['bucket']): string {
    return `dailyCheckin.journal.bucket.${bucket}`;
  }

  relativeLabelText(day: JournalDay): string {
    if (day.relativeLabel === '__today__')
      return this.t('dailyCheckin.journal.today');
    if (day.relativeLabel === '__yesterday__')
      return this.t('dailyCheckin.journal.yesterday');
    return '';
  }

  formatDuration(ms?: number): string {
    if (!ms || ms < 0) return '0:00';
    const total = Math.round(ms / 1000);
    const m = Math.floor(total / 60);
    const s = total % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  }

  // ---- Voice playback ----
  isPlaying(v: JournalVoice): boolean {
    return this.playingVoice === v && !!this.audioEl && !this.audioEl.paused;
  }

  async toggleVoice(v: JournalVoice): Promise<void> {
    if (this.isPlaying(v)) {
      this.audioEl?.pause();
      return;
    }
    // Resume same clip.
    if (this.playingVoice === v && this.audioEl && v.audioUrl) {
      this.audioEl.play().catch(() => {});
      return;
    }
    this.stopAudio();
    if (!v.audioUrl) {
      v.loadingAudio = true;
      const url = await this.dailyCheckinService.getAudioUrl(
        v.checkinId,
        v.entryIndex,
      );
      v.loadingAudio = false;
      if (!url) return;
      v.audioUrl = url;
    }
    this.audioEl = new Audio(v.audioUrl);
    this.playingVoice = v;
    this.audioEl.play().catch((e) => {
      console.warn('Audio playback failed:', e);
      this.reporter.report(e, 'checkin');
    });
  }

  private stopAudio(): void {
    if (this.audioEl) {
      this.audioEl.pause();
      this.audioEl.src = '';
      this.audioEl = null;
    }
    this.playingVoice = null;
  }

  // ---- Delete a day's record(s) (§7 privacy: user-deletable anytime) ----
  askDeleteDay(day: JournalDay): void {
    this.journalDays.forEach((d) => (d.confirmingDelete = false));
    day.confirmingDelete = true;
  }

  cancelDeleteDay(day: JournalDay): void {
    day.confirmingDelete = false;
  }

  async confirmDeleteDay(day: JournalDay): Promise<void> {
    if (day.deleting) return;
    day.deleting = true;
    try {
      const results = await Promise.all(
        day.checkinIds.map((id) => this.dailyCheckinService.deleteCheckin(id)),
      );
      if (results.every(Boolean)) {
        this.stopAudio();
        // Reload so buckets/week-summary recompute from the remaining records.
        await this.loadJournal();
      }
    } finally {
      day.deleting = false;
      day.confirmingDelete = false;
    }
  }

  // ---- Transcript edit (§4.3) ----
  startEditTranscript(v: JournalVoice): void {
    v.editing = true;
    v.draft = v.transcript;
  }

  cancelEditTranscript(v: JournalVoice): void {
    v.editing = false;
    v.draft = undefined;
  }

  async saveTranscript(v: JournalVoice): Promise<void> {
    if (this.savingTranscript) return;
    this.savingTranscript = true;
    try {
      const next = (v.draft ?? '').trim();
      const ok = await this.dailyCheckinService.updateTranscript(
        v.checkinId,
        v.entryIndex,
        next,
      );
      if (ok) {
        v.transcript = next;
        v.editing = false;
        v.draft = undefined;
      }
    } finally {
      this.savingTranscript = false;
    }
  }

  get doneTipTextKey(): string {
    return this.doneTipKey
      ? `wellnessTip.tips.${this.doneTipKey}`
      : '';
  }

  private showDoneView(): void {
    this.doneTipKey = this.wellnessTips.pickUnseen();
    this.wellnessTips.markSeen(this.doneTipKey);
    this.view = 'done';
  }

  private persistVoiceState(): void {
    if (this.view !== 'voice') return;
    try {
      const payload = {
        stepIndex: this.stepIndex,
        voiceResults: this.voiceResults,
      };
      sessionStorage.setItem(this.VOICE_STATE_KEY, JSON.stringify(payload));
    } catch {}
  }

  private clearVoiceState(): void {
    try {
      sessionStorage.removeItem(this.VOICE_STATE_KEY);
    } catch {}
  }

  private restoreVoiceState(): void {
    try {
      const raw = sessionStorage.getItem(this.VOICE_STATE_KEY);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (
        !data ||
        !Array.isArray(data.voiceResults) ||
        data.voiceResults.length !== this.voiceSteps.length
      ) {
        this.clearVoiceState();
        return;
      }
      this.voiceResults = data.voiceResults;
      this.stepIndex = Math.min(
        Math.max(0, Number(data.stepIndex) || 0),
        this.voiceSteps.length - 1,
      );
      this.resetVoiceRecordingUI();
      const current = this.voiceResults[this.stepIndex];
      if (current && (current.transcript || current.audioToken)) {
        this.voiceStatus = 'done';
      }
      this.view = 'voice';
    } catch {
      this.clearVoiceState();
    }
  }

}
